/**
 * Money and distance arithmetic.
 *
 * Everything is integer cents and integer metres. Floats are only ever
 * produced at the presentation boundary. An invoice whose line totals do not
 * sum exactly to its gross total will be rejected by EN 16931 validation, so
 * the rounding rule has to be one place and one place only.
 */

/** Round half away from zero — the convention German invoicing expects. */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Net cents plus VAT at `ratePercent`, rounded to whole cents. */
export function vatFromNet(netCents: number, ratePercent: number): number {
  return roundHalfUp((netCents * ratePercent) / 100);
}

/** Extract the VAT contained in a gross amount. */
export function vatFromGross(grossCents: number, ratePercent: number): number {
  return roundHalfUp((grossCents * ratePercent) / (100 + ratePercent));
}

/** The net portion of a gross amount. */
export function netFromGross(grossCents: number, ratePercent: number): number {
  return grossCents - vatFromGross(grossCents, ratePercent);
}

export function grossFromNet(netCents: number, ratePercent: number): number {
  return netCents + vatFromNet(netCents, ratePercent);
}

/**
 * Line total, net. Quantity may be fractional (hours, part-kilometres) but the
 * result is always whole cents.
 */
export function lineNetCents(quantity: number, unitPriceNetCents: number): number {
  return roundHalfUp(quantity * unitPriceNetCents);
}

export interface VatBucket {
  ratePercent: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
}

/**
 * Group lines by VAT rate and total each bucket separately.
 *
 * § 14 Abs. 4 Nr. 7 UStG requires the consideration to be broken down by tax
 * rate, and EN 16931 rule BR-CO-18 requires at least one VAT breakdown group.
 * VAT is calculated on the summed net per rate, not per line, because
 * per-line rounding drifts.
 */
export function summariseVat(
  lines: ReadonlyArray<{ netCents: number; vatRatePercent: number }>,
): VatBucket[] {
  const byRate = new Map<number, number>();
  for (const line of lines) {
    byRate.set(line.vatRatePercent, (byRate.get(line.vatRatePercent) ?? 0) + line.netCents);
  }
  return [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([ratePercent, netCents]) => {
      const vatCents = vatFromNet(netCents, ratePercent);
      return { ratePercent, netCents, vatCents, grossCents: netCents + vatCents };
    });
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

const plainNumberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `1234567` becomes `12.345,67 €`. */
export function formatEur(cents: number): string {
  return eurFormatter.format(cents / 100);
}

/** Same value without the currency symbol, for table columns and CSV. */
export function formatAmount(cents: number): string {
  return plainNumberFormatter.format(cents / 100);
}

/**
 * Parse German or plain decimal input into cents.
 *
 * Accepts `12,50`, `12.50`, `1.234,56`, `1234.56` and `12`. Returns null when
 * the input cannot be read as a number, so callers can show a field error
 * rather than silently booking a zero.
 */
export function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim().replace(/\s|€/g, '');
  if (trimmed === '') return null;

  const lastComma = trimmed.lastIndexOf(',');
  const lastDot = trimmed.lastIndexOf('.');
  let normalised: string;

  if (lastComma > lastDot) {
    // German: dots are thousands separators, comma is the decimal point.
    normalised = trimmed.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    normalised = trimmed.replace(/,/g, '');
  } else {
    normalised = trimmed;
  }

  const value = Number(normalised);
  if (!Number.isFinite(value)) return null;
  return roundHalfUp(value * 100);
}

// ---------------------------------------------------------------------------
// Distance
// ---------------------------------------------------------------------------

const kmFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** `23700` becomes `23,7`. */
export function formatKm(meters: number): string {
  return kmFormatter.format(meters / 1000);
}

export function parseKmToMeters(input: string): number | null {
  const cents = parseAmountToCents(input);
  if (cents === null) return null;
  // parseAmountToCents gives hundredths; kilometres to metres is a factor of 10.
  return roundHalfUp(cents * 10);
}

export function metersToKm(meters: number): number {
  return meters / 1000;
}
